import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import {
  DownloadRounded,
  EditRounded,
  FlagRounded,
  PlayArrowRounded,
  DeleteRounded,
  BlockRounded,
  CancelRounded,
  TuneRounded,
} from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import dataSenses from '@src/services/dataSenses';
import apis from '@src/apis';
import useIpLocation from '@src/hooks/useLocation';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import { downloadFileUsingUrl } from '@src/utils/file';
import { DOWNLOAD_QUOTA_EXCEEDED_CODE } from '@src/constants/tts';
import { useDispatch, useSelector } from 'react-redux';
import actions from '@src/redux/actions';
import { REQUEST_TYPE } from '@src/constants/request';
import { REQUEST_STATUS } from '@src/constants/voice';
import useRequestAnimationStatus from '@src/hooks/useRequestAnimationStatus';
import { MAX_AUDIO_RETENTION_PERIOD } from '@src/constants';
import { COLOR } from '@src/styles/color';
import { AUDIO_EFFECTS_URL, LANDING_PAGE_URL } from '@src/configs';
import { CATEGORY_TYPE, PLATFORM_TYPE } from '@src/constants/support';
import { StyledDrawer, StyledListAction } from './index.style';
import DeleteRequestDialog from './Actions/DeleteRequestDialog';
import EditTitleRequestDialog from './Actions/EditTitleRequestDialog';
import ReportErrorRequestDialog from './Actions/ReportErrorRequestDialog';

const DELETE_REQUEST = 'delete_request';
const EDIT_TITLE_REQUEST = 'edit_title_request';
const REPORT_ERROR_REQUEST = 'report_error_request';

const ActionDrawer = ({
  open,
  onClose,
  request,
  updateRequest,
  deleteRequest,
  onRowClick,
}) => {
  const { ip } = useIpLocation();
  const { t } = useTranslation();
  const { getFeatureValue } = useFeatureFlags();
  const dispatch = useDispatch();
  const {
    id: requestId,
    title,
    audioLink,
    retentionPeriod = MAX_AUDIO_RETENTION_PERIOD,
    createdAt,
    userId,
    voice,
  } = request;
  const { displayStatus } = useRequestAnimationStatus(request);
  const { user } = useSelector((state) => state.auth);

  const [dialogState, setDialogState] = useState({
    [DELETE_REQUEST]: false,
    [EDIT_TITLE_REQUEST]: false,
    [REPORT_ERROR_REQUEST]: false,
  });

  const audioExpired = moment().isAfter(
    moment(createdAt).add(retentionPeriod, 'days'),
  );

  const useNewContactSupport = getFeatureValue(
    FEATURE_KEYS.NEW_CONTACT_SUPPORT,
    {
      ip,
      userId: user?.id,
      email: user?.email,
      phoneNumber: user?.phoneNumber,
    },
  );

  const isDisabledAudio =
    !audioLink || audioExpired || displayStatus !== REQUEST_STATUS.SUCCESS;

  const openDialog = (dialogName) => {
    setDialogState((prevState) => ({ ...prevState, [dialogName]: true }));
  };

  const closeDialog = (dialogName) => {
    setDialogState((prevState) => ({ ...prevState, [dialogName]: false }));
  };

  const handleDownloadAudio = async (e) => {
    e.stopPropagation();
    dataSenses.sendDownloadAudioEvent({
      userId,
      requestType: REQUEST_TYPE.STUDIO,
    });
    const res = await apis.requests.getPresignedAudioUrl(requestId);

    if (res?.status) {
      const audioUrl = res.result?.audio;
      downloadFileUsingUrl(audioUrl);
      onClose();
      return;
    }

    if (res?.errorCode === DOWNLOAD_QUOTA_EXCEEDED_CODE) {
      dispatch(actions.user.updateShowBlockDownloadDialog(true));
      return;
    }

    dispatch(
      actions.noti.push({
        severity: 'error',
        message: 'downloadError',
        value: title,
      }),
    );
  };

  const handlePlayAudio = () => {
    // dispatch(actions.audioPlayer.updateStatus(true));
    onRowClick(request.id);
  };

  const handleAudioEffect = () =>
    window.open(`${AUDIO_EFFECTS_URL}/?audio=${audioLink}`, '_blank');
  const handleOpenErrorReport = () => {
    const ticketMetadata = {
      requestId,
      voiceCode: voice.code,
      userId: user.id,
    };
    const base64TicketMetadata = btoa(JSON.stringify(ticketMetadata));
    const encodedTicketMetadata = encodeURIComponent(base64TicketMetadata);

    if (useNewContactSupport) {
      window.open(
        `${LANDING_PAGE_URL}/support?metadata=${encodedTicketMetadata}&platform=${PLATFORM_TYPE.MOBILE_WEB}&category=${CATEGORY_TYPE.TTS}`,
        '_blank',
      );
    } else {
      openDialog(REPORT_ERROR_REQUEST);
    }
  };
  const commonActions = [
    {
      icon: FlagRounded,
      color: COLOR.darkBlue,
      label: 'errorReport',
      onClick: handleOpenErrorReport,
    },
    {
      icon: EditRounded,
      color: COLOR.darkBlue,
      label: 'editTitle',
      onClick: () => openDialog(EDIT_TITLE_REQUEST),
    },
    {
      icon: DeleteRounded,
      color: '#FC5555',
      label: 'delete',
      onClick: () => openDialog(DELETE_REQUEST),
    },
  ];

  const failureActions = commonActions;

  const disabledAudioActions = [
    {
      icon: BlockRounded,
      color: '#FC5555',
      label: t('invalidMobileAudios', { expireTime: retentionPeriod }),
      onClick: () => {},
    },
    ...commonActions,
  ];

  const enabledAudioActions = [
    {
      icon: DownloadRounded,
      color: COLOR.blueV2,
      label: 'downloadAudio',
      onClick: handleDownloadAudio,
    },
    {
      icon: PlayArrowRounded,
      color: COLOR.darkBlue,
      label: 'playAudo',
      onClick: handlePlayAudio,
    },
    {
      icon: TuneRounded,
      color: COLOR.darkBlue,
      label: 'effectGuide',
      onClick: handleAudioEffect,
    },
    ...commonActions,
  ];

  let moreActions;
  if (
    displayStatus === REQUEST_STATUS.FAILURE ||
    displayStatus === REQUEST_STATUS.IN_PROGRESS
  )
    moreActions = failureActions;
  else if (isDisabledAudio) moreActions = disabledAudioActions;
  else moreActions = enabledAudioActions;

  return (
    <StyledDrawer anchor="bottom" open={open} onClose={onClose}>
      <Box className="drawer-title">
        <Typography className="title">{request?.title}</Typography>
        <CancelRounded sx={{ color: COLOR.darkV2[32] }} onClick={onClose} />
      </Box>
      <Box className="drawer-content">
        <StyledListAction>
          {moreActions.map((action) => (
            <Box
              key={action.label}
              className="action"
              role="button"
              tabIndex="0"
              onClick={action.onClick}
            >
              <action.icon sx={{ color: action.color }} />
              <Typography sx={{ color: action.color }}>
                {t(action.label)}
              </Typography>
            </Box>
          ))}
        </StyledListAction>
      </Box>
      {dialogState[DELETE_REQUEST] && (
        <DeleteRequestDialog
          request={request}
          open={dialogState[DELETE_REQUEST]}
          onClose={() => closeDialog(DELETE_REQUEST)}
          onCloseActionDrawer={onClose}
          deleteRequest={deleteRequest}
        />
      )}
      {dialogState[EDIT_TITLE_REQUEST] && (
        <EditTitleRequestDialog
          request={request}
          open={dialogState[EDIT_TITLE_REQUEST]}
          onClose={() => closeDialog(EDIT_TITLE_REQUEST)}
          onCloseActionDrawer={onClose}
          updateRequest={updateRequest}
        />
      )}
      {dialogState[REPORT_ERROR_REQUEST] && (
        <ReportErrorRequestDialog
          request={request}
          open={dialogState[REPORT_ERROR_REQUEST]}
          onClose={() => closeDialog(REPORT_ERROR_REQUEST)}
        />
      )}
    </StyledDrawer>
  );
};

export default ActionDrawer;
